import { Injectable, inject, type OnDestroy, type Type } from '@angular/core';
import type {
  Message,
  Notification,
  NotificationTypeDefinition,
} from '@clean-code/models';
import type { ID } from '@clean-code/models/common';
import { ENV_TOKEN, type EnvConfig } from '@clean-code/models/common';
import {
  AuthInjectorToken,
  type IAuthService,
} from '@clean-code/shared/auth/util-auth';
import { HubConnectionState } from '@microsoft/signalr';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { takeUntil } from 'rxjs/operators';
import { SignalRSettings } from '../application/signalr.settings';
import { SignalRService } from './signalr.service';

interface NotificationState {
  messages: Message[];
  unreadCount: number;
  connectionState: HubConnectionState;
}

const initialState: NotificationState = {
  messages: [],
  unreadCount: 0,
  connectionState: HubConnectionState.Disconnected,
};

export const store = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store) => ({
    setMessages(messages: Message[]) {
      const unreadCount = messages.filter((m) => !m.isRead).length;
      patchState(store, { messages, unreadCount });
    },
    setConnectionState(state: HubConnectionState) {
      patchState(store, { connectionState: state });
    },
    setMessagesRead(ids: ID[]) {
      const messages = store.messages();
      messages.forEach((message) => {
        if (ids.includes(message.id)) {
          message.isRead = true;
        }
      });
      const unreadCount = messages.filter((m) => !m.isRead).length;
      patchState(store, { messages, unreadCount });
    },
  })),
);

@Injectable({ providedIn: 'root' })
export class NotificationService extends SignalRService implements OnDestroy {
  private notificationTypeDefinitions: NotificationTypeDefinition[] = [];
  private store = inject(store);
  constructor() {
    const env = inject(ENV_TOKEN) as EnvConfig;
    const authService = inject<IAuthService>(AuthInjectorToken);

    super(
      new SignalRSettings({
        url: `${env.signalR.api}hubs/notification`,
        options: {
          accessTokenFactory: () => authService.getAccessToken(),
          withCredentials: true,
        },
        name: 'NotificationService',
      }),
    );

    this.onConnected$
      .pipe(takeUntil(this.closeSubject))
      .subscribe(this.onConnectionStateChanged.bind(this));
  }

  // Expose state signals
  public readonly messages = this.store.messages;
  public readonly unreadCount = this.store.unreadCount;
  public readonly connectionState = this.store.connectionState;

  //NotificationTypeRegistration
  public registerNotificationType(definition: NotificationTypeDefinition) {
    if (
      this.notificationTypeDefinitions.find((x) => x.name === definition.name)
    ) {
      throw new Error(
        `NotificationType for '${definition.name}' already exists!`,
      );
    }

    this.notificationTypeDefinitions.push(definition);
  }

  public getNotificationType(name: string): Type<Notification> {
    const type = this.notificationTypeDefinitions.find((x) => x.name === name);
    if (!type) {
      console.error(`Notification Item Type for ${name} not found!`);
      return undefined;
    }
    return type.componentType;
  }

  public setNotificationRead(id: ID): void {
    this.setNotificationsRead([id]);
  }

  public setNotificationsRead(ids: ID[]): void {
    void this.hubConnection.invoke('setread', ids);
  }

  public setAllAsRead(): void {
    this.setNotificationsRead(this.messages().map((x) => x.id));
  }

  protected registerHubHandlers(): void {
    this.hubConnection.on('refresh', (data: Message[]) => {
      //string to date
      data.forEach((x) => {
        x.createDate = new Date(x.createDate);
      });
      this.store.setMessages(data);
    });

    this.hubConnection.on('add', (data: Message[]) => {
      //string to date
      data.forEach((x) => {
        x.createDate = new Date(x.createDate);
      });
      const currentMessages = this.messages();
      this.store.setMessages([...currentMessages, ...data]);
    });

    this.hubConnection.on('setread', (data: ID[]) => {
      this.store.setMessagesRead(data);
    });
  }

  protected removeHubHandlers(): void {
    this.hubConnection.off('refresh');
    this.hubConnection.off('add');
    this.hubConnection.off('setread');
  }

  private onConnectionStateChanged(connectionState: HubConnectionState) {
    this.store.setConnectionState(connectionState);
    if (connectionState === HubConnectionState.Connected) {
      void this.hubConnection.invoke('refresh');
    } else {
      this.store.setMessages([]);
    }
  }
}
